import { Box } from "@mui/material";
import { AccessTime as ClockIcon } from "@mui/icons-material";
import { useRef } from "react";
import { v2, nums } from "../ui/v2Tokens";

type Props = {
  mealName: string;
  timeFrom: string;
  timeTo: string;
  onChange: (changes: { timeFrom?: string; timeTo?: string }) => void;
};

/**
 * The meal's time WINDOW, as one quiet chip rather than two form fields.
 *
 * A meal header carries a name, a time, totals and three controls; drawing the time as a pair
 * of bordered inputs made it the loudest thing in the row. Here it reads as text until you
 * reach for it.
 *
 * ── direction ──
 * The chip itself flows RTL with the rest of the header, so the START time sits on the right
 * where a Hebrew reader begins. Each field's own "07:00" is still rendered LTR by the browser
 * — a clock time is not a Hebrew string — which is exactly the split we want and the reason
 * there is no `dir` override here.
 *
 * ── one clock, not three ──
 * `input[type="time"]` draws its own picker indicator, so leaving both visible next to an
 * icon of ours gave three clocks for one range. The native ones are hidden and our single
 * clock opens the picker instead, which also lets the pair share one affordance.
 */
const timeSx = {
  width: { xs: "62px", md: "46px" },
  border: "none",
  background: "transparent",
  // 16px on a phone, and the width follows it. Below 16 iOS Safari zooms the page in on focus and
  // does not zoom back out — which the viewport meta used to hide by banning zoom outright.
  fontSize: { xs: "16px", md: "12.5px" },
  fontFamily: "inherit",
  color: "inherit",
  padding: 0,
  outline: "none",
  ...nums,
  // the picker still opens — from the clock beside these, see `openPicker`
  "&::-webkit-calendar-picker-indicator": { display: "none" },
  "&:focus": { color: v2.ink },
};

const MealTimeRange = ({ mealName, timeFrom, timeTo, onChange }: Props) => {
  const fromRef = useRef<HTMLInputElement>(null);

  /** `showPicker` is Chrome 99+/Safari 16+; anywhere else, focusing the field is the fallback
   *  and the time is still fully editable by typing and arrow keys. */
  const openPicker = () => {
    const input = fromRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else input.focus();
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "4px 8px",
        borderRadius: v2.rSm,
        color: v2.ink3,
        transition: "background .12s, color .12s",
        "&:hover, &:focus-within": { background: v2.lineSoft, color: v2.ink2 },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={`בחר שעות — ${mealName}`}
        onClick={openPicker}
        sx={{
          display: "grid",
          placeItems: "center",
          border: "none",
          background: "transparent",
          color: "inherit",
          padding: 0,
          flex: "none",
          cursor: "pointer",
          lineHeight: 0,
          // the clock is the only way to the picker, so it has to be reachable by thumb
          width: { xs: "34px", md: "auto" },
          height: { xs: "34px", md: "auto" },
        }}
      >
        <ClockIcon sx={{ fontSize: 14 }} />
      </Box>
      <Box
        component="input"
        type="time"
        ref={fromRef}
        aria-label={`שעת התחלה — ${mealName}`}
        value={timeFrom}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ timeFrom: e.target.value })}
        sx={timeSx}
      />
      <Box component="span" aria-hidden sx={{ fontSize: "12px", flex: "none" }}>
        –
      </Box>
      <Box
        component="input"
        type="time"
        aria-label={`שעת סיום — ${mealName}`}
        value={timeTo}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ timeTo: e.target.value })}
        sx={timeSx}
      />
    </Box>
  );
};

export default MealTimeRange;
